import { WorkPlanningPanel } from '@/components/ai/work-planning';
import { useProjects } from '@/hooks/use-projects';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckSquare,
    Search,
    Calendar,
    Clock,
    AlertCircle,
    CheckCircle2,
    Circle,
    ChevronRight,
    Filter,
    FolderKanban,
} from 'lucide-react';
import { format, isToday, isTomorrow, isPast, isThisWeek } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useMyTasks, useUpdateTaskDynamic } from '@/hooks/use-tasks';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';
import type { TaskStatus, TaskPriority } from '@/lib/schemas';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const statusConfig = {
    TODO: { label: 'To Do', icon: Circle, color: 'text-foreground-muted' },
    IN_PROGRESS: { label: 'In Progress', icon: Clock, color: 'text-blue-500' },
    IN_REVIEW: { label: 'In Review', icon: AlertCircle, color: 'text-amber-500' },
    DONE: { label: 'Done', icon: CheckCircle2, color: 'text-green-500' },
    CANCELLED: { label: 'Cancelled', icon: Circle, color: 'text-foreground-muted line-through' },
} as const;

const priorityConfig = {
    NONE: { label: 'None', color: 'bg-slate-500/20 text-slate-400' },
    LOW: { label: 'Low', color: 'bg-slate-500/20 text-slate-400' },
    MEDIUM: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
    HIGH: { label: 'High', color: 'bg-amber-500/20 text-amber-400' },
    URGENT: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' },
} as const;

export function TasksPage() {
    const { t, language } = useI18n();
    const navigate = useNavigate();
    const projects = useProjects();
    const [planningProject, setPlanningProject] = useState('');
    const effectiveProject = planningProject || projects.data?.find(project => project.role !== 'VIEWER')?.id || '';
    const [searchQuery, setSearchQuery] = useState('');
    // Default to showing incomplete tasks when no filter is applied
    const [statusFilter, setStatusFilter] = useState<TaskStatus[]>(['TODO', 'IN_PROGRESS', 'IN_REVIEW']);
    const [priorityFilter, setPriorityFilter] = useState<TaskPriority[]>([]);

    const { data: myTasksData, isLoading } = useMyTasks({
        search: searchQuery || undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        priority: priorityFilter.length > 0 ? priorityFilter : undefined,
        sortBy: 'dueDate',
        sortOrder: 'asc',
    });
    const updateTask = useUpdateTaskDynamic();

    const tasks = myTasksData?.tasks || [];
    const stats = myTasksData?.stats || { assigned: 0, dueToday: 0, completedThisWeek: 0 };

    const formatDueDate = (dateString: string | null, taskStatus?: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const locale = language === 'fr' ? fr : enUS;
        if (isToday(date)) return t('task.today');
        if (isTomorrow(date)) return t('task.tomorrow');
        // Only show overdue if task is not completed
        if (isPast(date) && !isToday(date) && taskStatus !== 'DONE' && taskStatus !== 'CANCELLED') {
            return format(date, 'MMM d', { locale }) + ` (${t('task.overdue').toLowerCase()})`;
        }
        if (isThisWeek(date)) return format(date, 'EEEE', { locale });
        return format(date, 'MMM d', { locale });
    };

    const getDueDateColor = (dateString: string | null, taskStatus?: string) => {
        if (!dateString) return 'text-foreground-muted';
        const date = new Date(dateString);
        // Only show red for overdue if task is not completed
        if (isPast(date) && !isToday(date) && taskStatus !== 'DONE' && taskStatus !== 'CANCELLED') return 'text-red-500';
        if (isToday(date)) return 'text-amber-500';
        return 'text-foreground-secondary';
    };

    const toggleStatusFilter = (status: TaskStatus) => {
        setStatusFilter(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const togglePriorityFilter = (priority: TaskPriority) => {
        setPriorityFilter(prev =>
            prev.includes(priority)
                ? prev.filter(p => p !== priority)
                : [...prev, priority]
        );
    };

    const handleStatusChange = (taskId: string, projectId: string, newStatus: TaskStatus) => {
        updateTask.mutate({
            taskId,
            projectId,
            data: {
                status: newStatus,
                completedAt: newStatus === 'DONE' ? new Date().toISOString() : null,
            },
        });
    };

    return (
        <AppLayout >
            <div className="p-4 sm:p-6 md:p-8">
                <div className="mx-auto max-w-7xl">
                    {/* Header */}
                    <div className="mb-6 sm:mb-8 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('tasks.myTasks')}</h1>
                        <p className="mt-1 text-sm sm:text-base text-white/70">
                            {t('tasks.noTasksDescription')}
                        </p>
                    </div>

                    <div className="mb-4 space-y-2">{projects.data?.some(project => project.role !== 'VIEWER') && <select aria-label={language === 'fr' ? 'Projet à planifier' : 'Planning project'} value={effectiveProject} onChange={e => setPlanningProject(e.target.value)} className="rounded border border-white/10 bg-surface/70 p-2 text-sm">{projects.data.filter(project => project.role !== 'VIEWER').map(project => <option key={project.id} value={project.id}>{project.name}</option>)}</select>}{effectiveProject && <WorkPlanningPanel projectId={effectiveProject} onTask={id => navigate(`/projects/${effectiveProject}?task=${id}`)} onAsk={text => navigate(`/projects/${effectiveProject}?aiPrompt=${encodeURIComponent(text)}`)} />}</div>
                    {/* Search and filters */}
                    <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4">
                        <div className="relative flex-1 sm:max-w-md">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
                            <Input
                                placeholder={t('common.search') + '...'}
                                className="pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {/* Status Filter */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                                        <Filter className="h-4 w-4 mr-2" />
                                        {t('tasks.status')}
                                        {statusFilter.length > 0 && (
                                            <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                                {statusFilter.length}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {Object.entries(statusConfig).map(([key, config]) => (
                                        <DropdownMenuCheckboxItem
                                            key={key}
                                            checked={statusFilter.includes(key as TaskStatus)}
                                            onCheckedChange={() => toggleStatusFilter(key as TaskStatus)}
                                        >
                                            <config.icon className={cn('h-4 w-4 mr-2', config.color)} />
                                            {config.label}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Priority Filter */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        {t('tasks.priority')}
                                        {priorityFilter.length > 0 && (
                                            <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                                                {priorityFilter.length}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    {Object.entries(priorityConfig).map(([key, config]) => (
                                        <DropdownMenuCheckboxItem
                                            key={key}
                                            checked={priorityFilter.includes(key as TaskPriority)}
                                            onCheckedChange={() => togglePriorityFilter(key as TaskPriority)}
                                        >
                                            <Badge className={cn('mr-2', config.color)}>{config.label}</Badge>
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {(statusFilter.length > 0 || priorityFilter.length > 0) && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setStatusFilter([]);
                                        setPriorityFilter([]);
                                    }}
                                >
                                    {t('common.clear')}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Stats cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/20">
                                    <CheckSquare className="h-5 w-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.assigned}</p>
                                    <p className="text-sm text-white/70">{t('dashboard.assignedTasks')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/20">
                                    <Clock className="h-5 w-5 text-amber-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.dueToday}</p>
                                    <p className="text-sm text-white/70">{t('dashboard.dueToday')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/20">
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.completedThisWeek}</p>
                                    <p className="text-sm text-white/70">{t('dashboard.completedThisWeek')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tasks list */}
                    {isLoading ? (
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-12">
                            <div className="flex flex-col items-center justify-center text-center">
                                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mb-4" />
                                <p className="text-white/70">{t('common.loading')}</p>
                            </div>
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-8 sm:p-12">
                            <div className="flex flex-col items-center justify-center text-center">
                                <CheckSquare className="h-12 sm:h-16 w-12 sm:w-16 text-white/50 mb-4" />
                                <h2 className="text-lg sm:text-xl font-semibold text-white mb-2">
                                    {searchQuery || statusFilter.length > 0 || priorityFilter.length > 0
                                        ? t('task.noTasksDescription')
                                        : t('tasks.noTasks')}
                                </h2>
                                <p className="text-white/70 mb-6 max-w-md text-sm sm:text-base">
                                    {searchQuery || statusFilter.length > 0 || priorityFilter.length > 0
                                        ? t('project.tryDifferentSearch')
                                        : t('tasks.noTasksDescription')}
                                </p>
                                {!searchQuery && statusFilter.length === 0 && priorityFilter.length === 0 && (
                                    <Button asChild>
                                        <Link to="/projects">
                                            <FolderKanban className="h-4 w-4 mr-2" />
                                            {t('nav.projects')}
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl backdrop-blur-md bg-white/5 border border-white/10 overflow-hidden">
                            <div className="divide-y divide-white/10">
                                {tasks.map((task) => {
                                    const status = statusConfig[task.status] || statusConfig.TODO;
                                    const priority = priorityConfig[task.priority] || priorityConfig.MEDIUM;
                                    const StatusIcon = status.icon;

                                    return (
                                        <div
                                            key={task.id}
                                            className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 hover:bg-white/10 transition-colors"
                                        >
                                            {/* Status toggle */}
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="flex-shrink-0 hover:scale-110 transition-transform">
                                                        <StatusIcon className={cn('h-5 w-5', status.color)} />
                                                    </button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start">
                                                    {Object.entries(statusConfig).map(([key, config]) => (
                                                        <DropdownMenuItem
                                                            key={key}
                                                            onSelect={() => handleStatusChange(task.id, task.projectId, key as TaskStatus)}
                                                        >
                                                            <config.icon className={cn('h-4 w-4 mr-2', config.color)} />
                                                            {config.label}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>

                                            {/* Task info */}
                                            <div className="flex-1 min-w-0">
                                                <Link
                                                    to={`/projects/${task.projectId}?task=${task.id}`}
                                                    className="font-medium text-white hover:text-primary transition-colors line-clamp-1 text-sm sm:text-base"
                                                >
                                                    {task.title}
                                                </Link>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Link
                                                        to={`/projects/${task.projectId}`}
                                                        className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors"
                                                    >
                                                        {task.project.imageUrl ? (
                                                            <img
                                                                src={task.project.imageUrl}
                                                                alt={task.project.name}
                                                                className="h-4 w-4 rounded object-cover"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="h-4 w-4 rounded flex items-center justify-center text-[10px]"
                                                                style={{ backgroundColor: task.project.color || '#6366f1' }}
                                                            >
                                                                {task.project.iconEmoji || '📁'}
                                                            </div>
                                                        )}
                                                        <span className="truncate max-w-[100px] sm:max-w-none">{task.project.name}</span>
                                                    </Link>
                                                    {task.subtaskCount > 0 && (
                                                        <span className="text-xs text-white/50 hidden sm:inline">
                                                            · {task.subtaskCount} {t('tasks.subtasks').toLowerCase()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Priority - hidden on mobile */}
                                            <Badge className={cn('flex-shrink-0 hidden sm:flex', priority.color)}>
                                                {priority.label}
                                            </Badge>

                                            {/* Due date */}
                                            {task.dueDate && (
                                                <div className={cn('flex items-center gap-1.5 text-xs sm:text-sm flex-shrink-0', getDueDateColor(task.dueDate, task.status))}>
                                                    <Calendar className="h-3 sm:h-4 w-3 sm:w-4" />
                                                    <span className="hidden sm:inline">{formatDueDate(task.dueDate, task.status)}</span>
                                                    <span className="sm:hidden">{format(new Date(task.dueDate), 'M/d', { locale: language === 'fr' ? fr : enUS })}</span>
                                                </div>
                                            )}

                                            {/* Navigate arrow */}
                                            <Link
                                                to={`/projects/${task.projectId}?task=${task.id}`}
                                                className="flex-shrink-0 text-white/50 hover:text-white transition-colors"
                                            >
                                                <ChevronRight className="h-5 w-5" />
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
}
